import authority from '@/services/AuthService'
import type {
  DuplicationStatus,
  EventStoryLinkCreation,
  EventStoryWithSettings,
  StoryAPIDetail,
  StoryAPIList,
  StoryCreation,
  StoryTitleCheck,
  StoryUpdate,
} from '#/Story'
import type { PaginationAPIList } from '#/index'
import type { FeatureCollection, Polygon } from 'geojson'
import type { AxiosResponse } from 'axios'
import { type Queries, queriesReduce } from '@/utils/queries'
import type { TimelineServerData } from '@vue-unistra/timelinejs'
import type { CollaborationRoleType } from '#/Collaboration'

export const addDOI = async (id: string, doi: string): Promise<void> => {
  await authority.patch(`/api/stories/${id}/set_doi/`, { doi })
}

export const linkEventAndStory = async (storyId: string, events: EventStoryLinkCreation[]): Promise<void> => {
  await authority.patch(`/api/stories/${storyId}/add_events/`, { events })
}

export const unlinkEventAndStory = async (storyId: string, events: EventStoryLinkCreation[]): Promise<void> => {
  await authority.patch(`/api/stories/${storyId}/remove_events/`, { events })
}

export const updateStoryMap = async (id: string, geo: FeatureCollection<Polygon, null>): Promise<StoryAPIDetail> => {
  return await (
    await authority.patch(`/api/stories/${id}/`, { geo })
  ).data
}

export const updateStoryStatus = async (id: string, newStatus: boolean): Promise<AxiosResponse<StoryAPIDetail>> => {
  return await authority.patch<StoryAPIDetail>(`/api/stories/${id}/public_status/`, { isPublic: newStatus })
}

export const updateDuplicationStatus = async (id: string, status: DuplicationStatus): Promise<DuplicationStatus> => {
  const response = await authority.patch<StoryAPIDetail>(`/api/stories/${id}/duplication_status/`, {
    duplicationStatus: status.toUpperCase(),
  })

  return response.data.duplicationStatus as DuplicationStatus
}

export const getDefaultStory = async (): Promise<StoryAPIDetail> => {
  return (await authority.get('/api/stories/default/')).data
}

export const getStoryDetail = async (id: string): Promise<StoryAPIDetail> => {
  return (await authority.get(`/api/stories/${id}/`)).data
}

export const getStories = async (queries: Queries[] = []): Promise<PaginationAPIList<StoryAPIList>> => {
  return (await authority.get<PaginationAPIList<StoryAPIList>>(`/api/stories/${queriesReduce(queries)}`)).data
}

export const getStoryEvents = async (
  storyId: string,
  page?: number,
  rowsPerPage?: number,
): Promise<PaginationAPIList<EventStoryWithSettings>> => {
  const url = new URL(`/api/stories/${storyId}/events/`, import.meta.env.VITE_APP_BACK_URL)
  url.searchParams.append('page', (page ?? 1).toString())
  url.searchParams.append(
    'page_size',
    rowsPerPage === 0 || rowsPerPage === undefined ? `10000` : rowsPerPage.toString(),
  )
  return (await authority.get<PaginationAPIList<EventStoryWithSettings>>(`${url.toString()}`)).data
}

export const storySearch = async (
  search: string,
  page: number | undefined,
  rowsPerPage: number | undefined,
  eventId?: string,
): Promise<PaginationAPIList<StoryAPIList>> => {
  const url = new URL('/api/stories/', import.meta.env.VITE_APP_BACK_URL)
  url.searchParams.append('search', search)
  url.searchParams.append('page', (page ?? 1).toString())
  url.searchParams.append('page_size', rowsPerPage ? rowsPerPage.toString() : import.meta.env.VITE_APP_PAGINATION_LIMIT)
  if (eventId) url.searchParams.append('exclude_linked_to_event', eventId)
  const results = await authority.get(`${url.toString()}`)

  return results.data
}

export const storyTitleCheck = async (storyTitle: string): Promise<StoryTitleCheck[]> => {
  const url = new URL('/api/stories/check_title/', import.meta.env.VITE_APP_BACK_URL)
  url.searchParams.append('title', storyTitle)
  return (await authority.get<StoryTitleCheck[]>(`${url.toString()}`)).data
}

export const storyCreation = async (story: StoryCreation): Promise<StoryAPIDetail> => {
  return (await authority.post<StoryAPIDetail>('/api/stories/', story)).data
}

export const storyUpdateFormData = async (formData: FormData): Promise<StoryAPIDetail> => {
  return (await authority.patch<StoryAPIDetail>(`/api/stories/${formData.get('id') as string}/`, formData)).data
}

export const storyUpdateState = async (story: StoryUpdate): Promise<StoryAPIDetail> => {
  return (await authority.patch<StoryAPIDetail>(`/api/stories/${story.id}/`, story)).data
}

export const storyDelete = async (id: string): Promise<void> => {
  return await authority.delete(`/api/stories/${id}/`)
}

export const getStoryTimeline = async (storyId: string): Promise<TimelineServerData> => {
  return (await authority.get<{ timeline: TimelineServerData }>(`/api/stories/${storyId}/timeline/`)).data?.timeline
}

export const duplicateStory = async (id: string, collaborationRole: CollaborationRoleType): Promise<StoryAPIDetail> => {
  return (await authority.post(`/api/stories/${id}/duplicate/`, { role: collaborationRole })).data
}
